import React, { useState } from 'react';
import OrderListTemplate from './components/OrderListTemplate';
import { MergedOrder, OrderTabScene } from '../../types/process/order';
import OrderTriggerActionMap, { OrderTriggerActionProvider, type OrderTriggerTarget } from './components/OrderTriggerActionMap';
import { buildOrderInfoLines, getOrderAuditDisplayName, useDefaultQueryFields } from './order.effect';
import { Button, Space, Typography } from 'antd';
import { useStaffMap } from '../../services/store/organization.store';
import ProfitEstimationModal from './components/modals/ProfitEstimationModal';
const { Text } = Typography;

type ProfitEstimationTriggerButtonProps = {
  orderNo: string;
  layout?: 'list' | 'table';
};

const ProfitEstimationTriggerButton: React.FC<ProfitEstimationTriggerButtonProps> = ({ orderNo, layout = 'list' }) => {
  const [open, setOpen] = useState(false);
  const isTable = layout === 'table';

  return (
    <>
      <Button
        type={isTable ? 'link' : 'primary'}
        size="small"
        block={!isTable}
        style={isTable ? { padding: 0 } : undefined}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
      >
        收益测算
      </Button>
      <ProfitEstimationModal
        orderNo={orderNo}
        visible={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const OrderList: React.FC = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const reloadList = () => setRefreshKey(k => k + 1);
  const tabScene = OrderTabScene.GroupAll;
  const queryFields = useDefaultQueryFields(tabScene);
  const { staffMap } = useStaffMap();

  const targets: OrderTriggerTarget[] = ['syncRrzuStatus', 'reportView', 'cancelRevoke'];

  const renderTableActions = (order: MergedOrder) => {
    const nodes = [
      <OrderTriggerActionMap layout="table" targets={targets} order={order} onReload={reloadList} />,
      <Button
        type="link"
        size="small"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          window.open(`/order/audit/${order.order.orderNo}`, '_blank');
        }}
      >
        查看审核详情
      </Button>,
      <ProfitEstimationTriggerButton orderNo={order.order.orderNo} layout="table" />
    ];
    return (
      <Space size={4} direction={nodes.length > 2 ? 'vertical' : 'horizontal'} style={{ alignItems: 'flex-start' }}>
        {nodes}
      </Space>
    );
  };

  const getAuditConclusion = (status?: number) => {
    if (status === 3) return '建议通过';
    if (status === 4) return '建议取消';
    return '-';
  };

  const getDecisionConclusion = (status?: number) => {
    if (status === 3) return '放单';
    if (status === 4) return '取消订单';
    return '-';
  };


  const renderListInfo = (order: MergedOrder) => {
    const lines = buildOrderInfoLines(order, staffMap, ['preprocessOperator', 'auditOperator', 'auditConclusion', 'decisionConclusion']);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {lines.map((line, idx) => (
          <Text key={idx} style={{ color: line.color || '#1677ff' }}>
            {line.label}: {line.value}
          </Text>
        ))}
      </div>
    );
  };

  const renderListActions = (order: MergedOrder) => (
    <Space direction="vertical" style={{ width: '100%' }}>
      <OrderTriggerActionMap layout="list" targets={targets} order={order} onReload={reloadList} />
      <Button
        type="default"
        size="small"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          window.open(`/order/audit/${order.order.orderNo}`, '_blank');
        }}
        style={{ width: '100%' }}
      >
        查看审核详情
      </Button>
      <ProfitEstimationTriggerButton orderNo={order.order.orderNo} layout="list" />
    </Space>
  );

  return (
    <OrderTriggerActionProvider onReload={reloadList}>
      <OrderListTemplate
        refreshKey={refreshKey}
        tabScene={OrderTabScene.GroupAll}
        defaultScene='tab_all'
        queryFields={queryFields}
        viewModes={[
          {
            mode: 'table',
            label: '表格页',
            showField: 'auditStep',
            renderActions: renderTableActions,
          },
          { mode: 'list', label: '产品页', renderActions: renderListActions, renderInfo: renderListInfo },
        ]}
      />
    </OrderTriggerActionProvider>
  );
};

export default OrderList;
